import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import removeDuplicateFilters from 'components/Dashboard/Filters/removeDuplicateFilters';
import useCompileFiltersFunction from 'components/Dashboard/Filters/useCompileFilters';
import useCompileInitialFilters from 'components/Dashboard/Filters/useCompileInitialFilters';
import useGetSectionFilters from 'components/Dashboard/Filters/useGetSectionFilters';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import useCreateSections from 'components/Dashboard/hooks/useCreateSections';
import useNumberOfUnseenItems from 'components/Dashboard/hooks/useNumberOfUnseenItems';
import useCreateParliamentLiveFilters from 'components/Dashboard/ParliamentLive/useCreateParliamentLiveFilters';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import time from 'utils/time';
import { store } from 'components/Store';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

dayjs.extend(utc);

function usePriorityUpdatesFunctions({
  setIsLoading = () => {},
  setSectionsElements = () => {},
  setFreshElements = () => {},
  setFreshSectionsElements = () => {},
  setElements = () => {},
  getElementsToRender = () => {},
  contentFilters,
  currentlyProcessing,
  setCurrentlyProcessing = () => {},
  noIntervalCall = false,
}) {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan, keywordsLists, team } = state;
  const { enableHandpickedContent } = activePlan;
  const { getSectionFilters } = useGetSectionFilters('PriorityUpdates');
  const sectionFilters = getSectionFilters();
  const { searchWithin, hash, filters } = sectionFilters ?? {};
  const totalResultsCall = useRef(null);

  const { createSections } = useCreateSections();
  const { numberOfUnseenItems } = useNumberOfUnseenItems();
  const { priorityUpdatesSection } = useMainSectionParameter();
  const { compileInitialFilters } = useCompileInitialFilters();
  const { compileFiltersFunction } = useCompileFiltersFunction();
  const { generalApiCall } = useGeneralApiCall();
  const { navigate } = useNavigate();

  const topicIdQuery = getUrlParam('topic-id');
  const list = keywordsLists.find((item) => item.id === (topicIdQuery === 'all' ? null : parseInt(topicIdQuery)));
  const disabledSections = ['stakeholder', 'RealtimeUtterance', 'Event'];
  const callCondition = keywordsLists?.length > 0;
  const defaultInterval = 30000; //ms
  const minutesPerCallParam = getUrlParam('minutesPerCall');
  const minutesPerCall = minutesPerCallParam ?? defaultInterval / 60000;
  const interval = minutesPerCallParam ? 60000 : defaultInterval;

  const { createParliamentLiveFilters } = useCreateParliamentLiveFilters();
  const { initialCallFirstDateValue, initialCallSecondDateValue } = createParliamentLiveFilters({});

  const [date, setDate] = useState(initialCallSecondDateValue);

  const filtersFields = {
    KeyUpdates: 'lastImporterUpdatedAtUtc',
    Consultations: 'createdAtUtc',
    Legislation: 'createdAtUtc',
    ParliamentaryRecord: 'lastImporterUpdatedAtUtc',
    LibraryMaterial: 'createdAtUtc',
    CommitteePublications: 'createdAtUtc',
    LobbyingMaterial: 'dateTime',
    Tweets: 'createdAtUtc',
  };

  const individualApiCall = async ({
    pageSize,
    section,
    source,
    requestPropertiesProps = { filters: [] },
    initialCall,
    initialCallFirstDate,
  }) => {
    const { type, name, filters, collapseField } = section;
    const referenceFilters = compileInitialFilters('reference').filter((filter) => filter.pages.includes(name));
    const hashFilters = compileFiltersFunction(referenceFilters);

    const minRelevanceScore = !!import.meta.env.VITE_APP_PRIORITY_UPDATES_MIN_RELEVANCE_SCORE
      ? import.meta.env.VITE_APP_PRIORITY_UPDATES_MIN_RELEVANCE_SCORE
      : '0.8';
    const apiType = type ?? name;

    const pathname = '/api/feed/query';
    const method = 'post';

    let requestProperties = {
      type: apiType,
      pageSize: pageSize ?? (parseInt(import.meta.env.VITE_APP_PRIORITY_UPDATES_PAGE_SIZE) || 10),
      pageNumber: 1,
      includeHighlights: true,
      keywordListId: list?.id ?? null,
      includeExplanations: true,
      filters: [
        { field: 'importanceScore', operator: 'num_gt', value: '0' },
        {
          field: 'relevanceScore',
          operator: 'num_gt',
          value: minRelevanceScore,
        },
        ...requestPropertiesProps?.filters,
      ],
      sorts: [
        { field: 'importanceScore', order: 'desc' },
        { field: 'relevanceScore', order: 'desc' },
        { field: 'feedItemId', order: 'desc' },
      ],
    };

    if (enableHandpickedContent && list?.isHandpicked && name === 'KeyUpdates') {
      requestProperties.handPickedContent = true;
    }

    if (filters) {
      requestProperties = { ...requestProperties, filters: [...requestProperties.filters, ...filters] };
    }

    if (hashFilters?.filters?.length > 0) {
      requestProperties = { ...requestProperties, filters: [...requestProperties.filters, ...hashFilters?.filters] };
    }

    if (collapseField) {
      requestProperties = { ...requestProperties, collapseField };
    }

    if (searchWithin) {
      requestProperties = { ...requestProperties, ...searchWithin };
    }

    const numberOfDays = parseInt(import.meta.env.VITE_APP_PRIORITY_UPDATES_NUMBER_OF_DAYS);
    if (
      import.meta.env.VITE_APP_PRIORITY_UPDATES_NUMBER_OF_DAYS &&
      !isNaN(numberOfDays) &&
      numberOfDays > 0 &&
      initialCall
    ) {
      // Calculate date in UTC: current date minus numberOfDays
      const utcDateString = initialCallFirstDate.subtract(numberOfDays, 'day').utc().format('YYYY-MM-DDTHH:mm:ss');
      requestProperties = {
        ...requestProperties,
        filters: [
          ...requestProperties.filters?.filter(
            (item) =>
              !(
                item.field === (filtersFields[section?.name] ?? 'addedToFeedUtc') &&
                item.operator.toLowerCase() === 'date_gte'
              )
          ),
          {
            field: filtersFields[section?.name] ?? 'addedToFeedUtc',
            value: utcDateString,
            operator: 'date_gte',
          },
        ],
      };
    }

    if (requestProperties.filters?.length > 0) {
      requestProperties.filters = removeDuplicateFilters(requestProperties.filters);
    }

    let results = await generalApiCall({
      pathname,
      method,
      requestProperties,
      notShowErrorMessage: true,
      needsAuthentication: true,
      requestSource: source,
    });
    let finalResults = results;
    if (!!finalResults?.highlights) {
      finalResults?.hits.forEach((item, index) => {
        item.highlights = finalResults?.highlights[index];
      });
    }
    if (!!finalResults?.matchedKeywordListIds) {
      finalResults?.hits.forEach((item, index) => {
        item.keywordListId = finalResults?.matchedKeywordListIds[index]
          ? finalResults?.matchedKeywordListIds[index]
          : null;
      });
    }
    return finalResults;
  };

  totalResultsCall.current = async ({ source, returnResult, pageSize }) => {
    if (callCondition) {
      const initialCallFirstDate = dayjs();
      setDate(initialCallSecondDateValue);
      try {
        if (currentlyProcessing?.state) {
          setIsLoading(true);
        }
        const sourceOptions = filters
          .filter((filter) => filter.field === 'contentSource')
          .map((filter) => filter.value);
        const sections = createSections();
        let sectionsToCall = sections.filter((section) => !disabledSections.includes(section.type));
        if (sourceOptions?.length > 0) {
          sectionsToCall = sectionsToCall.filter(
            (section) => sourceOptions.includes(section.type) || sourceOptions.includes(section.name)
          );
        }
        let sectionsElementsObject = {};
        let isReady = true;
        for (let i = 0; i < sectionsToCall.length; i++) {
          let section = sectionsToCall[i];
          const userContent = section?.name === 'LobbyingMaterial';
          const requestPropertiesProps = {
            filters: [
              {
                field: filtersFields[section?.name] ?? 'addedToFeedUtc',
                value: userContent ? time(initialCallFirstDateValue) : initialCallFirstDateValue,
                operator: 'DATE_GTE',
              },
              {
                field: filtersFields[section?.name] ?? 'addedToFeedUtc',
                value: userContent ? time(initialCallSecondDateValue) : initialCallSecondDateValue,
                operator: 'date_lt',
              },
            ],
          };
          let result = await individualApiCall({
            pageSize,
            section,
            source,
            requestPropertiesProps,
            initialCall: true,
            initialCallFirstDate,
          });
          if (result) {
            sectionsElementsObject[section.name] = result;
            if (result.isReady === false) {
              isReady = false;
            }
            if (i === sectionsToCall.length - 1) {
              setIsLoading(false);
              setCurrentlyProcessing({ state: isReady, attempt: currentlyProcessing?.attempt + 1 });
            }
          }
        }

        setSectionsElements(sectionsElementsObject);
        setElements(getElementsToRender(sectionsElementsObject, setSectionsElements, setElements));
        if (priorityUpdatesSection) {
          await numberOfUnseenItems(source);
        }
        if (returnResult && Object.keys(sectionsElementsObject).length > 0 && !source.signal.aborted) {
          return sectionsElementsObject;
        }
        if (!source.signal.aborted) {
          navigate({ search: window.location.search, hash: !!hash ? `#${hash}` : '' }, { replace: true });
        }
      } catch (error) {}
    } else {
      setIsLoading(false);
      setSectionsElements({});
      setElements([]);
      setFreshSectionsElements({});
      if (returnResult) {
        return {};
      }
    }
  };

  const intervallCallFunction = useRef(null);
  intervallCallFunction.current = async (source) => {
    if (callCondition) {
      const secondDateValue = dayjs(date).add(minutesPerCall, 'minute').format('YYYY-MM-DDTHH:mm:ss');

      try {
        const sourceOptions = filters
          .filter((filter) => filter.field === 'contentSource')
          .map((filter) => filter.value);
        const sections = createSections();
        let sectionsToCall = sections.filter((section) => !disabledSections.includes(section.type));
        if (sourceOptions?.length > 0) {
          sectionsToCall = sectionsToCall.filter(
            (section) => sourceOptions.includes(section.type) || sourceOptions.includes(section.name)
          );
        }
        let sectionsElementsObject = {};
        for (let i = 0; i < sectionsToCall.length; i++) {
          let section = sectionsToCall[i];
          const requestPropertiesProps = {
            filters: [
              {
                field: filtersFields[section?.name] ?? 'addedToFeedUtc',
                value: date,
                operator: 'DATE_GTE',
              },
              {
                field: filtersFields[section?.name] ?? 'addedToFeedUtc',
                value: secondDateValue,
                operator: 'date_lt',
              },
            ],
          };
          let result = await individualApiCall({ section, source, requestPropertiesProps });
          if (result) {
            sectionsElementsObject[section.name] = result;
            if (i === sectionsToCall.length - 1) {
              setDate(secondDateValue);
            }
          }
        }

        setFreshSectionsElements((currentValue) => {
          // Merge new sections with existing fresh sections
          const merged = { ...currentValue };
          for (const sectionName in sectionsElementsObject) {
            if (merged[sectionName]) {
              // Merge arrays (hits, feedItemFlags, etc.)
              for (const prop in sectionsElementsObject[sectionName]) {
                if (
                  Array.isArray(sectionsElementsObject[sectionName][prop]) &&
                  Array.isArray(merged[sectionName][prop])
                ) {
                  merged[sectionName][prop] = [
                    ...sectionsElementsObject[sectionName][prop],
                    ...merged[sectionName][prop],
                  ];
                } else {
                  merged[sectionName][prop] = sectionsElementsObject[sectionName][prop];
                }
              }
            } else {
              merged[sectionName] = sectionsElementsObject[sectionName];
            }
          }
          return merged;
        });
        setFreshElements((currentValue) => [
          ...currentValue,
          ...getElementsToRender(sectionsElementsObject, setFreshSectionsElements, null),
        ]);

        // Only navigate if the request wasn't aborted (user didn't navigate away)
        if (!source.signal.aborted) {
          navigate({ search: window.location.search, hash: !!hash ? `#${hash}` : '' }, { replace: true });
        }
      } catch (error) {}
    }
  };

  const intervalFunc = useRef(null);
  useEffect(() => {
    const source = new AbortController();
    if (currentlyProcessing?.state === true && !noIntervalCall) {
      intervalFunc.current = setInterval(() => intervallCallFunction.current(source), interval);
    }
    return () => {
      if (!!source) {
        source.abort('Event call canceled by the user.');
      }
      if (!!intervalFunc.current) {
        clearInterval(intervalFunc.current);
      }
    };
  }, [topicIdQuery, contentFilters, interval, date, currentlyProcessing, team?.teamId]);

  return { totalResultsCall: totalResultsCall.current };
}

export default usePriorityUpdatesFunctions;
